import { Button, Form, Input, message } from "antd";
import { LockOutlined, UserOutlined } from "@ant-design/icons";
import { Link, useNavigate } from "react-router-dom";

import {
  IMAGE_URL,
  MIN_PASSWORD_LENGTH,
  ROUTE,
  SIGNIN_BG,
} from "../../Constant";

import { useAuthChecker } from "../../Context";
import { useAuthCheckMutation } from "../../Services";
import { useAppBranding } from "../../Hook";

export interface LoginFormValues {
  username: string;
  password: string;
}

export const SignInPage: React.FC = () => {
  const [form] = Form.useForm<LoginFormValues>();
  const navigate = useNavigate();
  const { signIn } = useAuthChecker();
  const { appName } = useAppBranding();

  const loginMutation = useAuthCheckMutation();

  const handleSubmit = (values: LoginFormValues) => {
    if (loginMutation.isPending) return;

    loginMutation.mutate(values, {
      onSuccess: (res) => {
        if (!res?.success) {
          message.error(res?.message ?? "Invalid username or password");
          return;
        }

        const data = res?.data;

        signIn(data);
        message.success(data?.message ?? "Successfully signed in");

        navigate(ROUTE.loginSuccess, {
          replace: true,
        });
      },
      onError: (error: any) => {

        message.error(
          error?.response?.data?.message ??
            "Something went wrong. Please try again.",
        );
      },
    });
  };

  return (
    <div className="flex min-h-screen bg-white">
      <div className="flex flex-1 items-center justify-center px-6">
        <div className="w-full max-w-sm">
          <h1 className="mb-1 text-2xl font-semibold text-gray-900">
            Sign in to {appName}
          </h1>

          <p className="mb-8 text-sm text-gray-500">
            Enter your credentials to continue.
          </p>

          <Form<LoginFormValues>
            form={form}
            layout="vertical"
            requiredMark={false}
            autoComplete="off"
            onFinish={handleSubmit}
          >
            <Form.Item
              name="username"
              rules={[
                {
                  required: true,
                  message: "Please enter your username",
                },
              ]}
            >
              <Input
                size="large"
                autoFocus
                placeholder="Username"
                prefix={<UserOutlined />}
              />
            </Form.Item>

            <Form.Item
              name="password"
              rules={[
                {
                  required: true,
                  message: "Please enter your password",
                },
                {
                  min: MIN_PASSWORD_LENGTH,
                  message: `Password must be at least ${MIN_PASSWORD_LENGTH} characters`,
                },
              ]}
            >
              <Input.Password
                size="large"
                placeholder="Password"
                prefix={<LockOutlined />}
              />
            </Form.Item>

            <div className="mb-6 flex justify-end">
              <Link
                to={ROUTE.forgotPassword}
                className="text-xs text-blue-600 hover:underline"
              >
                Forgot password?
              </Link>
            </div>

            <Button
              htmlType="submit"
              type="primary"
              size="large"
              block
              loading={loginMutation.isPending}
            >
              Sign In
            </Button>
          </Form>
        </div>
      </div>

      {/* Right Section */}
      <div
        className="hidden lg:flex flex-1 bg-cover bg-center"
        style={{
          backgroundImage: `url(${IMAGE_URL}/${SIGNIN_BG})`,
        }}
      />
    </div>
  );
};

export default SignInPage;
